

/* eslint-disable react/prop-types */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pickNativeChatPhotos } from "../../../native/chatPhotoPicker";

// useChatAttachments
// Extracted from Chat.jsx (MVP: images only)
//
// Responsibilities:
// - Manage pending image attachments (preview URLs, add/remove/clear)
// - Validate allowed image types (MIME + extension fallback)
// - Cleanup object URLs on unmount
//
// Personal chats can disable attachments by passing { allowAttachments: false }

export default function useChatAttachments(options = {}) {
  const {
    allowAttachments = true,
    maxAttachments = 5,
  } = options || {};

  // ===== Media (images only) MVP =====
  const fileInputRef = useRef(null);
  const [pendingImages, setPendingImages] = useState([]);
  // pendingImages item: { id, file, url, name, size }

  // keep latest pendingImages for cleanup on unmount
  const pendingImagesRef = useRef([]);
  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);

  useEffect(() => {
    return () => {
      try {
        (pendingImagesRef.current || []).forEach((p) => {
          try {
            if (p?.url) URL.revokeObjectURL(p.url);
          } catch {
            // ignore
          }
        });
      } catch {
        // ignore
      }
    };
  }, []);

  const MAX_ATTACHMENTS = Number.isFinite(Number(maxAttachments))
    ? Number(maxAttachments)
    : 5;

  const ALLOWED_IMAGE_MIME = useMemo(
    () =>
      new Set([
        "image/jpeg",
        "image/jpg", // some browsers
        "image/png",
        "image/webp",
        "image/gif",
        "image/heic",
        "image/heif",
        "image/heic-sequence",
        "image/heif-sequence",
      ]),
    []
  );

  const isAllowedImageFile = useCallback(
    (file) => {
      if (!file) return false;

      const mime = (file.type || "").toLowerCase();
      if (ALLOWED_IMAGE_MIME.has(mime)) return true;

      // Fallback for browsers that provide empty/odd MIME types
      const name = (file.name || "").toLowerCase();
      const ext = name.includes(".") ? name.split(".").pop() : "";

      // Allow by extension fallback
      if (
        ext === "jpg" ||
        ext === "jpeg" ||
        ext === "png" ||
        ext === "webp" ||
        ext === "gif" ||
        ext === "heic" ||
        ext === "heif" ||
        ext === "heik"
      )
        return true;

      return false;
    },
    [ALLOWED_IMAGE_MIME]
  );

  const addImageFiles = useCallback(
    (rawFiles) => {
      if (!allowAttachments) return;

      const files = Array.from(rawFiles || []);
      const onlyImages = files.filter((file) => isAllowedImageFile(file));
      if (!onlyImages.length) return;

      setPendingImages((prev) => {
        const room = Math.max(0, MAX_ATTACHMENTS - prev.length);
        const slice = onlyImages.slice(0, room);
        const mapped = slice.map((file, idx) => {
          const id = `${Date.now()}_${Math.random()
            .toString(16)
            .slice(2)}_${idx}`;
          return {
            id,
            file,
            url: URL.createObjectURL(file),
            name: file?.name || "image",
            size: file?.size || 0,
          };
        });

        return mapped.length ? [...prev, ...mapped] : prev;
      });
    },
    [allowAttachments, isAllowedImageFile, MAX_ATTACHMENTS]
  );

  const removePendingImage = useCallback((id) => {
    setPendingImages((prev) => {
      const next = prev.filter((p) => p.id !== id);
      const removed = prev.find((p) => p.id === id);
      if (removed?.url) {
        try {
          URL.revokeObjectURL(removed.url);
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, []);

  const clearPendingImages = useCallback((imageIds = null) => {
    const idsToClear = Array.isArray(imageIds)
      ? new Set(imageIds.map((id) => String(id)))
      : null;

    setPendingImages((prev) => {
      const removed = idsToClear
        ? prev.filter((p) => idsToClear.has(String(p?.id)))
        : prev;

      removed.forEach((p) => {
        if (p?.url) {
          try {
            URL.revokeObjectURL(p.url);
          } catch {
            // ignore
          }
        }
      });

      if (!idsToClear) return [];
      if (!removed.length) return prev;
      return prev.filter((p) => !idsToClear.has(String(p?.id)));
    });
  }, []);

  const onPickImages = useCallback(
    (e) => {
      if (!allowAttachments) {
        // Ensure input can be re-used
        try {
          if (e?.target) e.target.value = "";
        } catch {
          // ignore
        }
        return;
      }

      const files = Array.from(e?.target?.files || []);
      // Allow selecting the same files again later
      try {
        if (e?.target) e.target.value = "";
      } catch {
        // ignore
      }

      if (!files.length) return;

      addImageFiles(files);
    },
    [allowAttachments, addImageFiles]
  );

  const openNativeImagePicker = useCallback(async (source = "library") => {
    const room = Math.max(
      0,
      MAX_ATTACHMENTS - Number(pendingImagesRef.current?.length || 0)
    );
    if (!allowAttachments || room <= 0) return true;

    const files = await pickNativeChatPhotos({ limit: room, source });
    if (files === null) return false;
    addImageFiles(files);
    return true;
  }, [MAX_ATTACHMENTS, addImageFiles, allowAttachments]);

  return {
    // config
    allowAttachments,
    MAX_ATTACHMENTS,

    // refs/state
    fileInputRef,
    pendingImages,

    // actions
    onPickImages,
    openNativeImagePicker,
    addImageFiles,
    removePendingImage,
    clearPendingImages,

    // helpers (optional)
    isAllowedImageFile,
    setPendingImages, // exposed for advanced cases; can remove later if you want stricter API
  };
}
